import { useCallback, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'

export function useUrlPage(
    key: string = 'page',
    defaultPage: number = 1,
): [number, (page: number) => void] {
    const [searchParams, setSearchParams] = useSearchParams()

    const raw = searchParams.get(key)
    const parsed = raw === null ? defaultPage : Number.parseInt(raw, 10)
    const page = Number.isNaN(parsed) || parsed < 1 ? defaultPage : parsed

    const setPage = useCallback(
        (next: number) => {
            const sanitized =
                Number.isNaN(next) || next < 1 ? defaultPage : next

            setSearchParams(
                prev => {
                    const params = new URLSearchParams(prev)
                    if (sanitized === defaultPage) {
                        params.delete(key)
                    } else {
                        params.set(key, String(sanitized))
                    }
                    return params
                },
                { replace: false },
            )
        },
        [key, defaultPage, setSearchParams],
    )

    return [page, setPage]
}

export function useResetPageOnChange(
    setPage: (page: number) => void,
    deps: ReadonlyArray<unknown>,
) {
    const isFirstRender = useRef(true)
    const serialized = JSON.stringify(deps)
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false
            return
        }
        setPage(1)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serialized])
}
